import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Home, Calendar, Ruler, Bed, Bath, User, Mail, Phone, Upload, CheckCircle, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { createValuationRequest } from '@/lib/api';

const ValuationPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    // Property Details
    location: '',
    address: '',
    property_type: '',
    ownership_type: '',
    lease_years_remaining: '',
    land_size: '',
    building_size: '',
    bedrooms: '',
    bathrooms: '',
    amenities: '',
    condition: '',
    additional_info: '',
    // Contact Details
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    preferred_contact: 'email',
  });

  const locations = ['Canggu', 'Berawa', 'Pererenan', 'Seminyak', 'Uluwatu', 'Bingin', 'Sanur', 'Ubud', 'Kuta', 'Jimbaran', 'Nusa Dua', 'Other'];
  const propertyTypes = [
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'land', label: 'Land Only' },
    { value: 'commercial', label: 'Commercial Property' },
    { value: 'boutique_hotel', label: 'Boutique Hotel / Guesthouse' },
  ];
  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold (Hak Milik)' },
    { value: 'leasehold', label: 'Leasehold (Hak Sewa)' },
    { value: 'hgb', label: 'HGB (Hak Guna Bangunan)' },
  ];
  const conditionOptions = [
    { value: 'excellent', label: 'Excellent - Like New' },
    { value: 'good', label: 'Good - Well Maintained' },
    { value: 'fair', label: 'Fair - Some Updates Needed' },
    { value: 'needs_work', label: 'Needs Renovation' },
  ];
  const contactMethods = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createValuationRequest(form);
      setSubmitted(true);
      toast.success('Valuation request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-warm-sand">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-card p-8 text-center"
          >
            <div className="w-16 h-16 bg-ubud-jungle/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-ubud-jungle" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-charcoal mb-4">
              Valuation Request Received!
            </h1>
            <p className="text-charcoal-light mb-6">
              Thank you for your interest in selling your property. Our team of local experts will review your information and send you a comprehensive valuation within <strong>24-48 hours</strong>.
            </p>
            <div className="bg-warm-sand rounded-xl p-4 mb-6">
              <p className="text-sm text-charcoal-light">
                We'll contact you via <strong>{form.preferred_contact}</strong> at <strong>{form.preferred_contact === 'email' ? form.email : form.preferred_contact === 'whatsapp' ? form.whatsapp : form.phone}</strong>
              </p>
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full px-8"
            >
              Back to Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-warm-sand">
      {/* Hero Section */}
      <section className="bg-charcoal text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4">
              What's Your Bali Property Worth?
            </h1>
            <p className="font-body text-lg text-white/80 max-w-2xl mx-auto">
              Get a free, no-obligation valuation from our local experts. We'll analyze current market conditions and comparable sales to give you an accurate estimate.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <p className="font-heading text-2xl font-semibold text-ubud-jungle">500+</p>
              <p className="text-sm text-charcoal-light">Properties Valued</p>
            </div>
            <div>
              <p className="font-heading text-2xl font-semibold text-ubud-jungle">24-48h</p>
              <p className="text-sm text-charcoal-light">Response Time</p>
            </div>
            <div>
              <p className="font-heading text-2xl font-semibold text-ubud-jungle">10+ Years</p>
              <p className="text-sm text-charcoal-light">Bali Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-card p-6 sm:p-8"
          >
            {/* Property Details Section */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-semibold text-charcoal mb-1 flex items-center gap-2">
                <Home className="w-5 h-5 text-ubud-jungle" />
                Property Details
              </h2>
              <p className="text-sm text-charcoal-light mb-6">Tell us about your property</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Location *</label>
                  <Select
                    value={form.location}
                    onValueChange={(v) => setForm(prev => ({ ...prev, location: v }))}
                    required
                  >
                    <SelectTrigger data-testid="valuation-location">
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Type */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Property Type *</label>
                  <Select
                    value={form.property_type}
                    onValueChange={(v) => setForm(prev => ({ ...prev, property_type: v }))}
                    required
                  >
                    <SelectTrigger data-testid="valuation-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Address */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-charcoal mb-1">Property Address</label>
                  <Input
                    placeholder="Street address or landmark"
                    value={form.address}
                    onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                    data-testid="valuation-address"
                  />
                </div>

                {/* Ownership Type */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Ownership Type *</label>
                  <Select
                    value={form.ownership_type}
                    onValueChange={(v) => setForm(prev => ({ ...prev, ownership_type: v }))}
                    required
                  >
                    <SelectTrigger data-testid="valuation-ownership">
                      <SelectValue placeholder="Select ownership" />
                    </SelectTrigger>
                    <SelectContent>
                      {ownershipTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Lease Years - Only show for leasehold */}
                {form.ownership_type === 'leasehold' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Years Remaining on Lease *</label>
                    <Input
                      type="number"
                      placeholder="e.g., 25"
                      value={form.lease_years_remaining}
                      onChange={(e) => setForm(prev => ({ ...prev, lease_years_remaining: e.target.value }))}
                      required
                      data-testid="valuation-lease-years"
                    />
                  </div>
                )}

                {/* Land Size */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Land Size (m²) *</label>
                  <Input
                    type="number"
                    placeholder="e.g., 500"
                    value={form.land_size}
                    onChange={(e) => setForm(prev => ({ ...prev, land_size: e.target.value }))}
                    required
                    data-testid="valuation-land-size"
                  />
                </div>

                {/* Building Size */}
                {form.property_type !== 'land' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Building Size (m²)</label>
                    <Input
                      type="number"
                      placeholder="e.g., 300"
                      value={form.building_size}
                      onChange={(e) => setForm(prev => ({ ...prev, building_size: e.target.value }))}
                      data-testid="valuation-building-size"
                    />
                  </div>
                )}

                {/* Bedrooms */}
                {form.property_type !== 'land' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Bedrooms</label>
                    <Input
                      type="number"
                      placeholder="e.g., 4"
                      value={form.bedrooms}
                      onChange={(e) => setForm(prev => ({ ...prev, bedrooms: e.target.value }))}
                      data-testid="valuation-bedrooms"
                    />
                  </div>
                )}

                {/* Bathrooms */}
                {form.property_type !== 'land' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Bathrooms</label>
                    <Input
                      type="number"
                      placeholder="e.g., 4"
                      value={form.bathrooms}
                      onChange={(e) => setForm(prev => ({ ...prev, bathrooms: e.target.value }))}
                      data-testid="valuation-bathrooms"
                    />
                  </div>
                )}

                {/* Condition */}
                {form.property_type !== 'land' && (
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Property Condition</label>
                    <Select
                      value={form.condition}
                      onValueChange={(v) => setForm(prev => ({ ...prev, condition: v }))}
                    >
                      <SelectTrigger data-testid="valuation-condition">
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        {conditionOptions.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Key Amenities */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-charcoal mb-1">Key Features & Amenities</label>
                  <Input
                    placeholder="e.g., Private pool, ocean view, staff quarters, garden"
                    value={form.amenities}
                    onChange={(e) => setForm(prev => ({ ...prev, amenities: e.target.value }))}
                    data-testid="valuation-amenities"
                  />
                </div>

                {/* Additional Info */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-charcoal mb-1">Additional Information</label>
                  <Textarea
                    placeholder="Any other details that might affect value (recent renovations, rental income, unique features, etc.)"
                    value={form.additional_info}
                    onChange={(e) => setForm(prev => ({ ...prev, additional_info: e.target.value }))}
                    rows={3}
                    data-testid="valuation-additional"
                  />
                </div>
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="mb-8 pt-6 border-t">
              <h2 className="font-heading text-xl font-semibold text-charcoal mb-1 flex items-center gap-2">
                <User className="w-5 h-5 text-ubud-jungle" />
                Your Contact Details
              </h2>
              <p className="text-sm text-charcoal-light mb-6">How should we send your valuation?</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-charcoal mb-1">Full Name *</label>
                  <Input
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="valuation-name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Email Address *</label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="valuation-email"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Phone Number</label>
                  <Input
                    type="tel"
                    placeholder="+62 812 3456 7890"
                    value={form.phone}
                    onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    data-testid="valuation-phone"
                  />
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">WhatsApp Number</label>
                  <Input
                    type="tel"
                    placeholder="+62 812 3456 7890"
                    value={form.whatsapp}
                    onChange={(e) => setForm(prev => ({ ...prev, whatsapp: e.target.value }))}
                    data-testid="valuation-whatsapp"
                  />
                </div>

                {/* Preferred Contact */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Preferred Contact Method *</label>
                  <Select
                    value={form.preferred_contact}
                    onValueChange={(v) => setForm(prev => ({ ...prev, preferred_contact: v }))}
                    required
                  >
                    <SelectTrigger data-testid="valuation-contact-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contactMethods.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full py-6 text-lg font-medium"
              data-testid="valuation-submit"
            >
              {loading ? 'Submitting...' : 'Get My Free Valuation'}
              {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
            </Button>

            <p className="text-xs text-charcoal-muted text-center mt-4">
              By submitting, you agree to be contacted by Lalita Properties regarding your valuation request.
              Your information will be kept confidential.
            </p>
          </motion.form>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-heading text-2xl font-semibold text-charcoal text-center mb-8">
            Why Get Your Valuation From Us?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-ubud-jungle/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-ubud-jungle" />
              </div>
              <h3 className="font-heading font-medium text-charcoal mb-2">Local Expertise</h3>
              <p className="text-sm text-charcoal-light">
                Deep knowledge of Bali's micro-markets, from Canggu to Uluwatu
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-ubud-jungle/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-ubud-jungle" />
              </div>
              <h3 className="font-heading font-medium text-charcoal mb-2">Accurate Data</h3>
              <p className="text-sm text-charcoal-light">
                Access to recent comparable sales and current market trends
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-ubud-jungle/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-6 h-6 text-ubud-jungle" />
              </div>
              <h3 className="font-heading font-medium text-charcoal mb-2">Personal Service</h3>
              <p className="text-sm text-charcoal-light">
                Direct consultation with experienced property advisors
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ValuationPage;
